import { getUserIdFromToken } from "./Auth";

/*
 * A small stale-while-revalidate cache for read-only GET data.
 *
 * Entries live in memory and in sessionStorage, so they survive navigating away and reloading the
 * tab but disappear when the tab is closed. Keys are scoped to the logged-in user, and logging out
 * clears everything. Concurrent requests for the same key share one HTTP call, which matters for
 * aggregate queries that take tens of seconds on the database.
 *
 * Personal data is cached with `persist: false`: it is kept in memory only, so it never outlives
 * the page in sessionStorage, whichever way the user is logged out.
 */

export interface CacheOptions {
	/** Also keep the entry in sessionStorage, so it survives a reload. Default true. */
	persist?: boolean;
}

export interface CacheEntry<T> {
	data: T;
	storedAt: number;
}

const STORAGE_PREFIX = "pm-cache:";

const memory = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();
// Bumped on clear, so a request that started before logout cannot write its result back.
let generation = 0;
// Bumped per key on invalidation, so a request that started before an edit cannot write the
// pre-edit data back over it.
const keyGenerations = new Map<string, number>();
const keyGeneration = (storageKey: string) =>
	keyGenerations.get(storageKey) ?? 0;

const scopedKey = (key: string) =>
	`${STORAGE_PREFIX}${getUserIdFromToken() || "anonymous"}:${key}`;

export const readCache = <T>(key: string): CacheEntry<T> | undefined => {
	const storageKey = scopedKey(key);
	const hit = memory.get(storageKey);
	if (hit) return hit as CacheEntry<T>;
	try {
		const raw = sessionStorage.getItem(storageKey);
		if (!raw) return undefined;
		const entry = JSON.parse(raw) as CacheEntry<T>;
		memory.set(storageKey, entry);
		return entry;
	} catch {
		return undefined;
	}
};

export const isFresh = (
	entry: CacheEntry<unknown> | undefined,
	ttlMs: number
): boolean => !!entry && Date.now() - entry.storedAt < ttlMs;

const writeCache = <T>(
	storageKey: string,
	data: T,
	persist: boolean
): CacheEntry<T> => {
	const entry = { data, storedAt: Date.now() };
	memory.set(storageKey, entry);
	if (!persist) return entry;
	try {
		sessionStorage.setItem(storageKey, JSON.stringify(entry));
	} catch {
		// Storage full or blocked: the in-memory copy still serves this session.
	}
	return entry;
};

/** Runs `fetcher` unless the same key is already in flight, and caches the result. */
export const fetchCached = <T>(
	key: string,
	fetcher: () => Promise<T>,
	{ persist = true }: CacheOptions = {}
): Promise<CacheEntry<T>> => {
	const storageKey = scopedKey(key);
	const pending = inFlight.get(storageKey);
	if (pending) return pending as Promise<CacheEntry<T>>;

	const startedIn = generation;
	const startedAtKey = keyGeneration(storageKey);
	const request = fetcher()
		.then((data) =>
			startedIn === generation &&
			startedAtKey === keyGeneration(storageKey)
				? writeCache(storageKey, data, persist)
				: { data, storedAt: Date.now() }
		)
		.finally(() => {
			if (inFlight.get(storageKey) === request) {
				inFlight.delete(storageKey);
			}
		});
	inFlight.set(storageKey, request);
	return request;
};

/**
 * Returns the cached data for `key` while it is younger than `ttlMs`, otherwise fetches it (sharing
 * any request already in flight). A failed fetch rejects and caches nothing.
 */
export const getCached = async <T>(
	key: string,
	ttlMs: number,
	fetcher: () => Promise<T>,
	options?: CacheOptions
): Promise<T> => {
	const cached = readCache<T>(key);
	if (cached && isFresh(cached, ttlMs)) return cached.data;
	return (await fetchCached(key, fetcher, options)).data;
};

/** Drops one key, for data the user has just changed. */
export const invalidateCache = (key: string) => {
	const storageKey = scopedKey(key);
	keyGenerations.set(storageKey, keyGeneration(storageKey) + 1);
	memory.delete(storageKey);
	inFlight.delete(storageKey);
	try {
		sessionStorage.removeItem(storageKey);
	} catch {
		// Storage blocked: nothing was persisted there either.
	}
};

export const clearRequestCache = () => {
	generation += 1;
	memory.clear();
	inFlight.clear();
	try {
		Object.keys(sessionStorage)
			.filter((key) => key.startsWith(STORAGE_PREFIX))
			.forEach((key) => sessionStorage.removeItem(key));
	} catch {
		// Storage blocked: nothing was persisted there either.
	}
};
